import type { ReactNode } from 'react';
import PublicLayout, { usePublicMeta } from './PublicLayout';
import { SITE, mailto } from './site';

function LegalPage({ title, intro, children }: { title: string; intro: string; children: ReactNode }) {
  usePublicMeta(`${title} · YADA`, intro);
  return (
    <PublicLayout>
      <article className="mx-auto max-w-[760px] px-4 py-14 sm:px-6">
        <div className="font-mono text-[11.5px] font-semibold uppercase tracking-[0.1em] text-accent-ink">Last updated {SITE.policyUpdated}</div>
        <h1 className="mt-3 font-display text-[40px] font-extrabold tracking-[-0.03em] text-ink">{title}</h1>
        <p className="mt-4 text-[16.5px] leading-relaxed text-muted">{intro}</p>
        <div className="legal mt-10 space-y-8 text-[15.5px] leading-relaxed text-muted-2 [&_h2]:font-display [&_h2]:text-[21px] [&_h2]:font-bold [&_h2]:text-ink [&_li]:mt-1.5 [&_p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-5">
          {children}
        </div>
      </article>
    </PublicLayout>
  );
}

const Contact = () => <a href={mailto('Privacy request')} className="font-semibold text-primary">{SITE.email}</a>;

export function Privacy() {
  return (
    <LegalPage title="Privacy policy"
      intro={`How ${SITE.company} collects, uses and protects information in the YADA student app, the YADA School panel and the YADA staff app.`}>
      <section>
        <h2>Who we are</h2>
        <p>
          YADA is operated by {SITE.company}, {SITE.city}. For any privacy question or request, write to <Contact />.
        </p>
      </section>
      <section>
        <h2>What we collect</h2>
        <ul>
          <li><b>Account details:</b> phone number, name, grade or field of study, school, and a password stored only as a secure hash.</li>
          <li><b>Learning activity:</b> exams taken, answers, scores, study time, notes, flashcards and study-plan settings.</li>
          <li><b>Content you send to the AI tutor:</b> your questions and any photos or documents you upload for it to read.</li>
          <li><b>School work:</b> worksheet answers, including photos of written work, and messages from your teachers.</li>
          <li><b>Payment records:</b> plan, amount and transaction reference. Payments are processed by our payment partners; we do not store card or wallet PINs.</li>
          <li><b>Device information:</b> a notification token so we can send reminders, plus basic app version and error information.</li>
          <li><b>Optional details you choose to share:</b> a parent or guardian’s contact for the weekly digest, career interests, target universities, and whether you appear to study buddies or on a school leaderboard.</li>
        </ul>
      </section>
      <section>
        <h2>How we use it</h2>
        <ul>
          <li>To run the app: save your progress, recommend practice and build your study plan.</li>
          <li>To answer AI requests. Text and files you send to the AI tutor are processed by Google’s Gemini models on Google Cloud to produce a reply.</li>
          <li>If you are linked to a school: to show your teachers, counsellors and school administrators your results, activity and alerts so they can support you.</li>
          <li>To send reminders and notifications you can switch off in Profile → Notifications.</li>
          <li>To keep the service secure, prevent abuse and fix problems.</li>
        </ul>
        <p>We do not sell personal information and we do not show third-party advertising.</p>
      </section>
      <section>
        <h2>Who can see your information</h2>
        <ul>
          <li><b>Your school</b>, only if you join it in the app. Staff see school-related data for students in their own school.</li>
          <li><b>Other students</b> see only what you opt into, such as a display name on a leaderboard or as a study buddy.</li>
          <li><b>Service providers</b> who host and run YADA for us (cloud hosting, AI processing, notifications, payments and messaging), under their own security and confidentiality terms.</li>
          <li><b>Authorities</b>, when Ethiopian law requires it.</li>
        </ul>
      </section>
      <section>
        <h2>Children and students</h2>
        <p>
          Many YADA users are secondary-school students. Schools that use YADA are responsible for informing students
          and parents. Parents or guardians may contact us to review or delete a student’s data.
        </p>
      </section>
      <section>
        <h2>Retention and deletion</h2>
        <p>
          We keep your data while your account is active. You can ask us to delete your account and its data at any
          time by writing to <Contact /> from the phone number or email linked to the account. We will complete the request within 30 days,
          except for records we must keep by law, such as payment records.
        </p>
      </section>
      <section>
        <h2>Security</h2>
        <p>
          Data travels over encrypted HTTPS connections. Access to school data is limited by role, and staff accounts
          can be signed out remotely. No system is perfectly secure; tell us at once if you notice anything wrong.
        </p>
      </section>
      <section>
        <h2>Changes</h2>
        <p>We will post any change to this policy on this page and update the date above.</p>
      </section>
    </LegalPage>
  );
}

export function Terms() {
  return (
    <LegalPage title="Terms of use"
      intro={`The rules for using YADA, provided by ${SITE.company}. By using the app or the school panel you agree to them.`}>
      <section>
        <h2>The service</h2>
        <p>
          YADA provides exam practice, AI study tools and school monitoring tools. We work to keep content accurate and
          aligned with the Ethiopian curriculum, but practice material and AI answers can contain mistakes and are not
          official examination content.
        </p>
      </section>
      <section>
        <h2>Your account</h2>
        <ul>
          <li>Keep your password private. You are responsible for activity on your account.</li>
          <li>School staff accounts are created by the school and must be used only for school work.</li>
          <li>Do not share, resell or copy YADA content or try to access data that is not yours.</li>
        </ul>
      </section>
      <section>
        <h2>Payments</h2>
        <p>
          Subscriptions and AI credits are paid in advance through the payment methods shown in the app. Prices are shown
          before you pay. Contact us at <a href={mailto('Payment question')} className="font-semibold text-primary">{SITE.email}</a> if a payment did not activate your plan.
        </p>
      </section>
      <section>
        <h2>Acceptable use</h2>
        <p>
          Do not use YADA to harass others, upload unlawful content, or disrupt the service. We may suspend accounts that
          break these rules.
        </p>
      </section>
      <section>
        <h2>Liability</h2>
        <p>
          YADA is provided as is. To the extent the law allows, {SITE.company} is not liable for exam outcomes or indirect
          losses arising from use of the service.
        </p>
      </section>
      <section>
        <h2>Governing law</h2>
        <p>These terms are governed by the laws of the Federal Democratic Republic of Ethiopia.</p>
      </section>
      <section>
        <h2>Contact</h2>
        <p>{SITE.company}, {SITE.city} · <a href={mailto()} className="font-semibold text-primary">{SITE.email}</a></p>
      </section>
    </LegalPage>
  );
}
